import { CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowRight } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import type { DivisionChange } from "@/lib/utils/division-diff";
import { getDivisionImagePath } from "@/lib/utils/division-images";
import { getUnitImagePath } from "@/lib/utils/unit-images";
import { UnitChangeCard } from "./UnitChangeCard";
import type { Unit } from "@/lib/types/unit";
import type { UnitWithWeapons } from "@/lib/utils/unit-weapons-joined";
import type { Division } from "@/lib/types/deck-builder";
import { extractDiffValues, type DiffResult } from "@/lib/utils/patch-diff";
import { shouldOmitField } from "./patch-notes-omit";
import { shouldShowField } from "./field-filter";

interface DivisionChangeCardProps {
  divisionChange: DivisionChange;
  unitMap?: Record<string, Unit>;
  divisionsMap?: Map<string, Division>;
  selectedFields?: Set<string>;
}

/**
 * Format value for display
 */
function formatValue(value: any): string {
  if (value === null || value === undefined) {
    return "N/A";
  }
  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }
  if (Array.isArray(value)) {
    return value.length > 0 ? `[${value.join(", ")}]` : "[]";
  }
  return String(value);
}

/**
 * Format veterancy bracket notation for display
 * Formats an array as bracket notation with spaces: [5, 3, 2, 0]
 */
function formatBracketNotation(array: number[] | undefined | null): string {
  if (!array || array.length === 0) {
    return "[]";
  }
  return `[${array.join(", ")}]`;
}

/**
 * Calculate actual availability bracket notation by multiplying multipliers by units per pack
 * This gives the actual quantities available at each veterancy level
 */
function calculateAvailabilityBracket(
  multipliers: number[] | undefined | null,
  unitsPerPack: number | undefined | null
): number[] | null {
  if (!multipliers || !unitsPerPack) {
    return null;
  }
  return multipliers.map(multiplier => Math.round(multiplier * unitsPerPack));
}

/**
 * Convert a Unit to UnitWithWeapons by adding an empty weapons array
 */
function unitToUnitWithWeapons(unit: Unit): UnitWithWeapons {
  const { weaponDescriptorName, ...unitWithoutWeaponDescriptor } = unit;
  return {
    ...unitWithoutWeaponDescriptor,
    weapons: [],
  };
}

/**
 * Check if a unit change would be visible after filtering
 * Uses the same logic as UnitChangeCard to determine visibility
 */
function hasVisibleChanges(diff: DiffResult, selectedFields?: Set<string>): boolean {
  // If no filtering is active, all changes are visible
  if (!selectedFields || selectedFields.size === 0) {
    return true;
  }

  if (!diff || typeof diff !== "object") {
    return false;
  }

  // Recursively check if there are any visible changes
  function checkDiff(obj: any, path: string = ""): boolean {
    if (!obj || typeof obj !== "object") {
      return false;
    }

    // Check if it's a direct diff array [oldValue, newValue]
    const diffValues = extractDiffValues(obj);
    if (diffValues) {
      const newPath = path || "";
      if (newPath && !shouldOmitField(newPath) && shouldShowField(newPath, selectedFields!)) {
        return true;
      }
      return false;
    }

    // Recursively traverse object
    for (const [key, value] of Object.entries(obj)) {
      const newPath = path ? `${path}.${key}` : key;
      
      // Skip omitted fields
      if (shouldOmitField(newPath)) {
        continue;
      }
      
      // Handle array diff format (for divisions, specialities, etc.)
      // Check if this is an array of diff markers like [['+', value], ['-', value]]
      // jsondiffpatch stores array diffs as arrays where each element is a diff marker
      if (Array.isArray(value) && value.length > 0) {
        // Check if first element is a diff marker array (format: ['+', value], ['-', value], etc.)
        const firstElement = value[0];
          if (Array.isArray(firstElement) && firstElement.length >= 2 && typeof firstElement[0] === "string") {
            // Array of diff markers - check if field matches filter
            // For specialities and divisions, check if the field name itself is selected
            const isSpecialitiesOrDivisions = key === "specialities" || key === "divisions" || newPath === "specialities" || newPath === "divisions";
            if (isSpecialitiesOrDivisions && selectedFields!.has(key)) {
              // Direct match on field name
              return true;
            }
            // Direct check: if the field name matches, or if shouldShowField says it matches
            if (selectedFields!.has(newPath) || shouldShowField(newPath, selectedFields!)) {
              return true;
            }
            continue;
          }
      }
      
      // Also handle the case where specialities/divisions is stored as an object with numeric keys
      // e.g., { specialities: { "0": ['+', value], "1": ['-', value] } }
      const keyIsNumeric = /^\d+$/.test(key);
      const isSimpleStringArrayField = path === "specialities" || path === "divisions";
      // Check if this is a diff marker (format: ['+', value] or ['-', value])
      const isDiffMarker = Array.isArray(value) && value.length >= 2 && typeof value[0] === "string" && (value[0] === '+' || value[0] === '-' || value[0] === '~');
      if (keyIsNumeric && isSimpleStringArrayField && isDiffMarker) {
        // This is a diff marker array under a simple string array field
        // Direct check: if the parent field name matches, or if shouldShowField says it matches
        if (selectedFields!.has(path) || shouldShowField(path, selectedFields!)) {
          return true;
        }
        continue;
      }
      
      // Also check if we're at the "specialities" or "divisions" key and the value is an object
      // jsondiffpatch may store array diffs as objects with _t: 'a' marker and numeric keys
      // Format: { specialities: { _t: 'a', 0: ['+', value], 1: ['-', value] } }
      if ((key === "specialities" || key === "divisions") && value && typeof value === "object" && !Array.isArray(value)) {
        // Check if this is a jsondiffpatch array diff (has _t: 'a' marker)
        const isArrayDiff = (value as any)._t === 'a';
        // Or check if it contains numeric keys with diff markers
        const hasDiffMarkers = Object.keys(value).some(k => {
          if (k === '_t') return false; // Skip the _t marker
          const v = (value as any)[k];
          return Array.isArray(v) && v.length >= 2 && typeof v[0] === "string" && (v[0] === '+' || v[0] === '-' || v[0] === '~');
        });
        
        if (isArrayDiff || hasDiffMarkers) {
          // Direct check: if the field name matches, or if shouldShowField says it matches
          if (selectedFields!.has(key) || selectedFields!.has(newPath) || shouldShowField(newPath, selectedFields!)) {
            return true;
          }
          continue;
        }
      }
      
      // Check if field matches filter or if we should recurse for nested paths
      let shouldRecurseForNested = false;
      if (value && typeof value === "object" && !Array.isArray(value)) {
        const normalizedPath = newPath.replace(/\[\d+\]/g, ".*").replace(/\.\d+(?=\.|$)/g, ".*");
        
        for (const selectedField of selectedFields!) {
          const normalizedSelected = selectedField.replace(/\[\d+\]/g, ".*").replace(/\.\d+(?=\.|$)/g, ".*");
          // Check if selected field starts with current path (reverse prefix match)
          if (normalizedSelected.startsWith(normalizedPath + ".") || 
              normalizedSelected.startsWith(normalizedPath + "[")) {
            shouldRecurseForNested = true;
            break;
          }
        }
      }
      
      const isNumericKey = /^\d+$/.test(key);
      const parentMatches = isNumericKey && path && shouldShowField(path, selectedFields!);
      const shouldShow = shouldShowField(newPath, selectedFields!) || parentMatches || shouldRecurseForNested;
      
      if (shouldRecurseForNested || parentMatches) {
        if (checkDiff(value, newPath)) {
          return true;
        }
        continue;
      }
      
      if (!shouldShow) {
        continue;
      }
      
      const valueDiffValues = extractDiffValues(value);
      if (valueDiffValues) {
        return true;
      }
      
      // Recursively check nested objects
      if (value && typeof value === "object" && !Array.isArray(value)) {
        if (checkDiff(value, newPath)) {
          return true;
        }
      } else if (Array.isArray(value) && value.length > 0 && typeof value[0] !== "string") {
        // Check array elements
        for (let i = 0; i < value.length; i++) {
          const arrayItemPath = `${newPath}[${i}]`;
          if (!shouldOmitField(arrayItemPath) && shouldShowField(arrayItemPath, selectedFields!)) {
            if (checkDiff(value[i], arrayItemPath)) {
              return true;
            }
          }
        }
      }
    }

    return false;
  }

  return checkDiff(diff);
}

export function DivisionChangeCard({ divisionChange, unitMap = {}, divisionsMap, selectedFields }: DivisionChangeCardProps) {
  const { division, addedUnits, removedUnits, availabilityChanges, unitChanges } = divisionChange;
  const divisionImagePath = getDivisionImagePath(division.descriptor);

  // Filter unit changes to only count visible ones
  const visibleUnitChanges = unitChanges.filter((change) => {
    if (!selectedFields || selectedFields.size === 0) {
      return true; // Show all when no filter is active
    }
    return hasVisibleChanges(change.diff, selectedFields);
  });

  // Filter availability changes to only count visible ones
  const visibleAvailabilityChanges = availabilityChanges.filter((change) => {
    if (!selectedFields || selectedFields.size === 0) {
      return true; // Show all when no filter is active
    }
    
    // Create synthetic diff to check visibility (same as what will be displayed)
    const syntheticDiff: any = {};
    const hasVeterancyChange = change.changes.numberOfUnitInPackXPMultiplier !== undefined;
    const hasUnitsPerPackChange = change.changes.numberOfUnitsInPack !== undefined;
    const showAvailability = hasVeterancyChange || hasUnitsPerPackChange;
    
    if (showAvailability) {
      const oldMultipliers = change.changes.numberOfUnitInPackXPMultiplier?.old ?? change.oldPack?.numberOfUnitInPackXPMultiplier;
      const oldUnitsPerPack = change.changes.numberOfUnitsInPack?.old ?? change.oldPack?.numberOfUnitsInPack;
      const newMultipliers = change.changes.numberOfUnitInPackXPMultiplier?.new ?? change.pack.numberOfUnitInPackXPMultiplier;
      const newUnitsPerPack = change.changes.numberOfUnitsInPack?.new ?? change.pack.numberOfUnitsInPack;
      
      let oldAvailabilityBracket: number[] | null = null;
      let newAvailabilityBracket: number[] | null = null;
      
      if (oldMultipliers && oldUnitsPerPack !== undefined) {
        oldAvailabilityBracket = calculateAvailabilityBracket(oldMultipliers, oldUnitsPerPack);
      }
      if (newMultipliers && newUnitsPerPack !== undefined) {
        newAvailabilityBracket = calculateAvailabilityBracket(newMultipliers, newUnitsPerPack);
      }
      
      if (oldAvailabilityBracket && newAvailabilityBracket) {
        syntheticDiff.availability = [formatBracketNotation(oldAvailabilityBracket), formatBracketNotation(newAvailabilityBracket)];
      }
    }
    
    if (change.changes.numberOfCards) {
      syntheticDiff.numberOfCards = [change.changes.numberOfCards.old, change.changes.numberOfCards.new];
    }
    if (change.changes.availableWithoutTransport !== undefined) {
      syntheticDiff.availableWithoutTransport = [change.changes.availableWithoutTransport.old, change.changes.availableWithoutTransport.new];
    }
    if (change.changes.availableTransportList) {
      syntheticDiff.availableTransportList = [change.changes.availableTransportList.old, change.changes.availableTransportList.new];
    }
    
    return hasVisibleChanges(syntheticDiff, selectedFields);
  });

  const hasChanges =
    addedUnits.length > 0 ||
    removedUnits.length > 0 ||
    visibleAvailabilityChanges.length > 0 ||
    visibleUnitChanges.length > 0;

  if (!hasChanges) {
    return null;
  }

  return (
    <Accordion type="single" collapsible className="w-full">
      <AccordionItem value={division.descriptor} className="border-2 rounded-lg overflow-hidden">
        <AccordionTrigger className="px-6 py-4 hover:no-underline">
          <div className="flex items-start gap-4 w-full">
            <div className="flex-shrink-0">
              <img
                src={divisionImagePath}
                alt={division.name}
                className="h-16 w-16 object-cover rounded border"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = "none";
                }}
              />
            </div>
            <div className="flex-1 min-w-0 text-left">
              <CardTitle className="text-lg font-semibold">{division.name}</CardTitle>
              <p className="text-sm text-muted-foreground mt-1">{division.descriptor}</p>
              <div className="mt-2 flex flex-wrap gap-2">
                {addedUnits.length > 0 && (
                  <Badge variant="default" className="bg-green-600">
                    +{addedUnits.length} Units Added
                  </Badge>
                )}
                {removedUnits.length > 0 && (
                  <Badge variant="destructive">
                    -{removedUnits.length} Units Removed
                  </Badge>
                )}
                {visibleAvailabilityChanges.length > 0 && (
                  <Badge variant="secondary">
                    {visibleAvailabilityChanges.length} Availability Changes
                  </Badge>
                )}
                {visibleUnitChanges.length > 0 && (
                  <Badge variant="secondary">
                    {visibleUnitChanges.length} Unit Stat Changes
                  </Badge>
                )}
              </div>
            </div>
          </div>
        </AccordionTrigger>
        <AccordionContent>
          <div className="px-6 pb-4">
            <div className="space-y-4">
          {/* Removed units */}
          {removedUnits.length > 0 && (
            <div>
              <h4 className="font-medium text-sm mb-2 text-red-600 dark:text-red-400">
                Units Removed from Division:
              </h4>
              <div className="flex flex-wrap gap-2">
                {removedUnits.map((unitDescriptor) => (
                  <Badge key={unitDescriptor} variant="outline" className="bg-red-50 text-red-700 dark:bg-red-950 dark:text-red-300">
                    {unitDescriptor}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* All unit changes (stat changes, availability changes, and added units) */}
          {(() => {
            // Convert visible availability changes to unit cards
            const availabilityUnitCards = visibleAvailabilityChanges
              .map((change) => {
                const unit = unitMap[change.unitDescriptor];
                if (!unit) return null;

                const unitWithWeapons = unitToUnitWithWeapons(unit);
                
                // Calculate availability bracket notation
                const hasVeterancyChange = change.changes.numberOfUnitInPackXPMultiplier !== undefined;
                const hasUnitsPerPackChange = change.changes.numberOfUnitsInPack !== undefined;
                const showAvailability = hasVeterancyChange || hasUnitsPerPackChange;
                
                let oldAvailabilityBracket: number[] | null = null;
                let newAvailabilityBracket: number[] | null = null;
                
                if (showAvailability) {
                  const oldMultipliers = change.changes.numberOfUnitInPackXPMultiplier?.old ?? change.oldPack?.numberOfUnitInPackXPMultiplier;
                  const oldUnitsPerPack = change.changes.numberOfUnitsInPack?.old ?? change.oldPack?.numberOfUnitsInPack;
                  const newMultipliers = change.changes.numberOfUnitInPackXPMultiplier?.new ?? change.pack.numberOfUnitInPackXPMultiplier;
                  const newUnitsPerPack = change.changes.numberOfUnitsInPack?.new ?? change.pack.numberOfUnitsInPack;
                  
                  if (oldMultipliers && oldUnitsPerPack !== undefined) {
                    oldAvailabilityBracket = calculateAvailabilityBracket(oldMultipliers, oldUnitsPerPack);
                  }
                  if (newMultipliers && newUnitsPerPack !== undefined) {
                    newAvailabilityBracket = calculateAvailabilityBracket(newMultipliers, newUnitsPerPack);
                  }
                }

                // Create synthetic diff object with all availability-related changes
                const syntheticDiff: any = {};
                
                // Add availability bracket notation if available
                if (showAvailability && oldAvailabilityBracket && newAvailabilityBracket) {
                  syntheticDiff.availability = [formatBracketNotation(oldAvailabilityBracket), formatBracketNotation(newAvailabilityBracket)];
                }
                
                // Add other changes from the availability change
                if (change.changes.numberOfCards) {
                  syntheticDiff.numberOfCards = [change.changes.numberOfCards.old, change.changes.numberOfCards.new];
                }
                if (change.changes.availableWithoutTransport !== undefined) {
                  syntheticDiff.availableWithoutTransport = [change.changes.availableWithoutTransport.old, change.changes.availableWithoutTransport.new];
                }
                if (change.changes.availableTransportList) {
                  syntheticDiff.availableTransportList = [change.changes.availableTransportList.old, change.changes.availableTransportList.new];
                }

                return {
                  unit: unitWithWeapons,
                  oldUnit: unitWithWeapons, // For availability-only changes, oldUnit is the same
                  diff: syntheticDiff,
                  changeType: "changed" as const,
                };
              })
              .filter((card) => card !== null);

            // Convert added units to unit cards
            const addedUnitCards = addedUnits
              .map((unitDescriptor) => {
                const unit = unitMap[unitDescriptor];
                if (!unit) return null;

                const unitWithWeapons = unitToUnitWithWeapons(unit);
                
                return {
                  unit: unitWithWeapons,
                  oldUnit: undefined,
                  diff: {},
                  changeType: "added" as const,
                };
              })
              .filter((card) => card !== null);

            // Combine all unit changes (use filtered lists)
            const allUnitCards = [
              ...visibleUnitChanges.map((change) => ({
                unit: change.unit,
                oldUnit: change.oldUnit,
                diff: change.diff,
                changeType: "changed" as const,
              })),
              ...availabilityUnitCards, // Already filtered to visible ones
              ...addedUnitCards, // Added units are always visible
            ];

            if (allUnitCards.length === 0) {
              return null;
            }

            return (
              <div>
                <h4 className="font-medium text-sm mb-2">Unit Changes:</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {allUnitCards.map((card, index) => (
                    <UnitChangeCard
                      key={index}
                      unit={card.unit}
                      oldUnit={card.oldUnit}
                      diff={card.diff}
                      changeType={card.changeType}
                      divisionsMap={divisionsMap}
                      selectedFields={selectedFields}
                    />
                  ))}
                </div>
              </div>
            );
          })()}
            </div>
          </div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}

